import React, {createRef, useContext, useEffect, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import {TablePagination, useTheme} from "@material-ui/core";

import SystemContext from '../../../SystemContext';
import {ColumnWidth} from "../../../blocks/ColumnHelper";
import {defaultComponents, MaterialTableBody} from '../../../controls/Material-Table-Components';
import { ComboSelect, MaskedNumber, MaskedText, UldAllocationSelect } from '../../../controls';
import ClientCompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";
import AirportLookup from "../../Airports/components/AirportsLookup";
import {
  useVTripAllotments,
} from "../../../services/trips/";
import FlightAllotmentBar from "./FlightAllotmentBar";
import CustomerAllotmentBar from "./CustomerAllotmentBar";


const FlightClientsAllotments = (_props) => {
  const theme = useTheme();
  const {isAuthenticated} = useAuth0();
  const props = {
    canEdit: true,
    ..._props
  }

  const tripUuid = props.tripUuid;
  const tripLegUuid = props.tripLegUuid;
  const maxPayload = props.maxPayload;

  const extraKeys = [tripUuid, tripLegUuid];
  const queryClient = useQueryClient();
  const allotments = useVTripAllotments({queryClient, extraKeys});

  const getTripClientColumns = (props) => {
    const baseColumn = {
      cellStyle: {padding: "8px 2px"},
      headerStyle: {padding: "8px 2px"},
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };

    const formatChars = {
      "U": "[A-Z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {
        ...baseColumn,
        ...ColumnWidth(350),
        cellStyle: {padding: "8px 2px 8px 12px"},
        headerStyle: {padding: "8px 2px 8px 12px"},
        title: "Client Company",
        field: "companyName",
        initialEditValue: "",
        editable: props.canEdit ? "onAdd" : "never",
        editComponent: (props) => (
          <ClientCompanyUuidLookup
            companyType={"CUSTOMER"}
            includeFreesale={true}
            value={props.value}
            returnType={"object"}
            onChange={(returnObject) => {
              if (returnObject.found) {
                const customer = returnObject.data
                const data = {
                  ...props.rowData,
                  companyName: customer.companyName,
                  companyUuid: customer.companyUuid,
                  companyCode: customer.companyCode,
                  branchCode: customer.branchCode
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, undefined, undefined, {textAlign: "center"}, {textAlign: "center"}, {textAlign: "center"}),
        title: "Origin",
        field: "aoo",
        sorting: false,
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(70,undefined, undefined, {textAlign: "center"}, {textAlign: "center"}, {textAlign: "center"}),
        title: "Dest.",
        field: "aod",
        sorting: false,
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(150),
        title: "Allotment Code",
        field: "allotment Code",
        sorting: false,
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(50)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            forceUpperCase={true}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(450,undefined, undefined, {paddingRight: 10}, {paddingRight: 10}, {paddingRight: 10}, ),
        title: "ULD Allocation",
        field: "uldAllocation",
        sorting: false,
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <UldAllocationSelect
            value={props.value}
            onChange={(record) => {
              props.onRowDataChange({...props.rowData, ...record});
            }}
          />
        ),
        render: (rowdata) => (
          <UldAllocationSelect
            value={rowdata.uldAllocation}
            readOnly={true}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(120,undefined, undefined, {paddingRight: 10}, {paddingRight: 10}, {paddingRight: 10}, ),
        title: "Allotment KG",
        field: "allotmentKg",
        type: "numeric",
        sorting: false,
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(6)}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (rowdata) => (
          <MaskedNumber
            displayType="text"
            value={rowdata.allotmentKg}
            fixedDecimalScale={true}
            readOnly={true}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(120,undefined, undefined, {paddingRight: 10}, {paddingRight: 10}, {paddingRight: 10}, ),
        title: "Booked CW",
        field: "totalChargeableWeight",
        type: "numeric",
        sorting: false,
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(6)}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (rowdata) => (
          <MaskedNumber
            displayType="text"
            value={rowdata.totalChargeableWeight}
            fixedDecimalScale={true}
            readOnly={true}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(400, 2, undefined, {paddingLeft: 20, paddingRight: 10}, {paddingLeft: 10, paddingRight: 10}, {paddingRight: 10}),
        title: "Booking Utilisation",
        field: "allotmentKg",
        filtering: false,
        render: rowData => {
          return ( <CustomerAllotmentBar tripLegUuid={rowData.tripLegUuid} companyUuid={rowData.companyUuid} customerAllotment={rowData.allotmentKg}/>);
        },
      },
    ];
  };

  const tableRef = createRef({state: {lastEditingRow: false}});
  const bodyRef = createRef();

  return (isAuthenticated &&
    <div style={{marginLeft: props.mode === "DIALOG"?0:50}}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          backgroundColor: "#fdfdfd",
          border: "2px solid #aaaaaa",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.childTableHeader.backgroundColor,
            color: theme.palette.childTableHeader.textColor,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          paging: false,
          pageSize: 5,
          detailPanelType: "single",
          padding: 'dense',
          toolbar: props.mode !== "DIALOG",
          search: props.mode !== "DIALOG",
          showTitle: props.mode !== "DIALOG",
        }}
        components={{
          ...defaultComponents,
          Body: (props) => (
            <MaterialTableBody
              {...props}
              ref={bodyRef}
              rowHeight={46}
              overscanRowCount={10}
              addVirtualScrolling={true}
              bodyHeight={allotments.data * 46}
              headerHeight={46}
            />
          ),
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
        }}
        title={`Flight Client Cargo Allotments ${maxPayload ? `(Max Payload: ${maxPayload.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})} Kg)` : ""}`}
        columns={getTripClientColumns({canEdit: false})}
        data={allotments.data}
        editable={{
          isEditable:false,
          isDeletable: false,
          onRowAdd: undefined,
          onRowUpdate: undefined,
          onRowDelete: undefined,
        }}
      />
    </div>
  );

}

export default FlightClientsAllotments;
